import { PropsWithChildren, useContext, useEffect, useState } from 'react';

import AuthenticatingScreen from '@features/auth/AuthenticatingScreen/AuthenticatingScreen';
import { UserDetailsContext } from '@shared/providers/UserDetailsProvider/providerSetup.ts';
import { loadCountryTheme } from '@shared/styles/app-theme/loadCountryTheme.ts';
import { UserCountryCode } from '@shared/types/countryCodes.ts';
import { applyDocumentCountryHead } from '@shared/utils/applyDocumentHead.ts';

export function CountryThemeProvider({ children }: PropsWithChildren) {
  const userDetails = useContext(UserDetailsContext);
  const [themeLoadedForCountryCode, setThemeLoadedForCountryCode] = useState<UserCountryCode | null | undefined>(
    undefined,
  );
  const isThemeReady = userDetails !== undefined && themeLoadedForCountryCode === userDetails.countryCode;

  useEffect(() => {
    if (!userDetails) {
      return;
    }

    let isCancelled = false;

    void loadCountryTheme(userDetails.countryCode).then(() => {
      if (isCancelled) {
        return undefined;
      }

      document.documentElement.dataset.countryTheme = userDetails.countryCode ?? 'default';
      applyDocumentCountryHead(userDetails.countryCode);
      setThemeLoadedForCountryCode(userDetails.countryCode);

      return undefined;
    });

    return () => {
      isCancelled = true;
    };
  }, [userDetails]);

  if (!isThemeReady) {
    return <AuthenticatingScreen />;
  }

  return children;
}
